import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'

import { APIResponse } from '@models/Common'
import { Consents, EmailCredentials } from '@models/Identity'
import { Survey } from '@models/Survey'
import { PredictionResults } from '@models/Prediction/Prediction'
import { requestInterceptor, errorInterceptor, responseInterceptor } from '@api/interceptors'
import {
  SubmitSurveyRequest,
  SubmitSandboxRequest,
  SubmitProbabilityRequest,
  SubmitCompleteSurveyRequest,
} from '@models/Survey/SubmitAnswersRequest'
import { RectificationRequest } from '@models/Prediction/RectificationResponse'
import { SearchMoviesResponse } from '@models/Movie'
import { PostalCodeDtoFactory, SearchCircumscriptionsResponse } from '@models/PostalCode'
import { SurveyStatRequest } from '@models/Survey/SurveyStatRequest'
import { ProbabilityResults } from '@models/Survey/Survey'
import { ForecastingRequest } from '@models/Prediction/ForecastingRequest'
import { CanadianPostalCodeDto } from '@models/CanadianPostalCode'
import { CategoryEdition } from '@models/Category'

const baseApiConfig: AxiosRequestConfig = {
  // responseType: 'json',
  // headers: {
  //   'Content-Type': 'application/json',
  //   'Cache-Control': 'public',
  // },
}

const client = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL as string,
  ...baseApiConfig,
})
client.interceptors.request.use(requestInterceptor)
client.interceptors.response.use(responseInterceptor, errorInterceptor)

const getSurvey = (surveyId: string): Promise<APIResponse<Survey>> => client.get(`/surveys/${surveyId}/`)

const getCategories = (options?: { includeInactive?: boolean }): Promise<APIResponse<CategoryEdition[]>> =>
  client.get('/categories/', {
    params: options?.includeInactive ? { include_inactive: '1' } : undefined,
  })

const giveConsent = (
  surveyId: string,
  consent: Consents,
): Promise<
  APIResponse<{
    visitorId: string
    surveyId: string
  }>
> => client.post(`/surveys/${surveyId}/consent/`, consent)

const submitAnswers = (surveyData: SubmitSurveyRequest): Promise<AxiosResponse<PredictionResults[]>> =>
  client.post<PredictionResults[]>(`/surveys/${surveyData.surveyId}/visitors/${surveyData.visitorId}/submit`, {
    answers: surveyData.answers,
    confirmation: surveyData.confirmation,
    language: surveyData.language,
    target_survey_id: surveyData.targetSurveyId,
  })

const submitEmail = (email: EmailCredentials): Promise<AxiosResponse<void>> => client.post('/emails', email)

const submitSandbox = (sandboxData: SubmitSandboxRequest): Promise<AxiosResponse<PredictionResults[]>> =>
  client.post<PredictionResults[]>(`/surveys/${sandboxData.surveyId}/visitors/${sandboxData.visitorId}/predict`, {
    answers: sandboxData.answers,
    target_survey_id: sandboxData.targetSurveyId,
  })

const submitCorrection = (correctionData: RectificationRequest): Promise<AxiosResponse<void>> =>
  client.post(`/surveys/${correctionData.surveyId}/visitors/${correctionData.visitorId}/correction`, {
    correction: correctionData.correction,
  })

const submitStats = (stats: SurveyStatRequest): Promise<AxiosResponse<void>> => client.post('/survey/stats/', stats)

const submitForecasting = (forecasting: ForecastingRequest): Promise<AxiosResponse<void>> => {
  let party: string | number | null = forecasting.party
  if (typeof party === 'string' && party !== 'another') {
    party = Number(party)
  }

  return client.post(`/surveys/${forecasting.surveyId}/visitors/${forecasting.visitorId}/bet`, {
    party,
  })
}

const searchMovies = (title: string, language: string): Promise<APIResponse<SearchMoviesResponse>> => {
  let lang = language ? language.toLocaleLowerCase() : 'fr'
  if (lang !== 'en' && lang !== 'fr') {
    lang = 'fr'
  }

  if (lang === 'fr') {
    lang = 'fr-CA'
  }

  return client.get<SearchMoviesResponse>('/movies', {
    params: {
      language: lang,
      query: title,
    },
  })
}

const searchBooks = (title: string): Promise<APIResponse<SearchBooksResponse>> => {
  return client.get<SearchBooksResponse>('/books', {
    params: {
      query: title,
    },
  })
}

const searchCircumscriptions = async (code: string): Promise<APIResponse<SearchCircumscriptionsResponse>> => {
  const response = await client.get<unknown>('/postal-codes', {
    params: {
      code,
    },
  })
  // Django returns a paginated { results: [...] } with snake_case fields (nm_circ).
  return {
    ...response,
    data: PostalCodeDtoFactory.fromApiPayload(response.data),
  }
}

const searchFederalElectionDistricts = (code: string): Promise<APIResponse<CanadianPostalCodeDto[]>> => {
  return client.get<CanadianPostalCodeDto[]>('/canadian-postal-codes', {
    params: {
      code,
    },
  })
}

const generatePortrait = (graphics: any[]) => {
  return client.post('/generate-portrait', { graphics })
}

const submitProbability = (probabilityData: SubmitProbabilityRequest): Promise<AxiosResponse<ProbabilityResults>> =>
  client.post<ProbabilityResults>(
    `/surveys/${probabilityData.surveyId}/visitors/${probabilityData.visitorId}/probability`,
    {
      votingProbability: probabilityData.probability,
    },
  )

const submitCompleteSurvey = ({ surveyId, visitorId }: SubmitCompleteSurveyRequest): Promise<AxiosResponse> =>
  client.post<ProbabilityResults>(`/surveys/${surveyId}/visitors/${visitorId}/complete-survey`, {})

export default {
  getSurvey,
  getCategories,
  giveConsent,
  submitAnswers,
  submitEmail,
  submitSandbox,
  submitStats,
  submitCorrection,
  submitForecasting,
  searchMovies,
  searchBooks,
  searchCircumscriptions,
  submitProbability,
  generatePortrait,
  submitCompleteSurvey,
  searchFederalElectionDistricts,
}
